import React from "react";
import TextField from "@mui/material/TextField";
import moment from "moment";
import { useFormikContext } from "formik";
import { usePopupState, bindTrigger, bindPopover } from "material-ui-popup-state/hooks";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import Typography from "@mui/material/Typography";

const styles = {
  monthButton: {
    borderRadius: 5,
    py: 0.5,
    px: 0,
  },
};

const getButtonStyle = isSelected => ({
  color: isSelected ? "white" : "black",
  backgroundColor: isSelected ? "primary.main" : "transparent",
  "&:hover": {
    backgroundColor: isSelected ? "primary.dark" : "action.hover",
  },
});

const getMonthDisplay = monthValue => {
  if (monthValue === "N/A") return "N/A";
  if (!monthValue) return "";
  return monthValue;
};

const getMonthName = monthNum => {
  if (!monthNum || isNaN(+monthNum)) return "";
  return moment()
    .month(monthNum - 1)
    .format("MMMM");
};

export default function MonthPicker({ name, label, disabled, textFieldRef }) {
  const { values, setFieldValue, errors, touched, submitCount } = useFormikContext();
  const popupState = usePopupState({ variant: "popover", popupId: `month-picker-${name}` });
  const errorMessage = errors[name];
  const showError = (touched[name] || submitCount > 0) && errorMessage;

  const handleMonthSelect = month => {
    setFieldValue(name, month);
    popupState.close();
  };

  const handleNASelect = () => {
    setFieldValue(name, "N/A");
    popupState.close();
  };

  return (
    <div>
      <TextField
        ref={textFieldRef}
        label={label}
        fullWidth
        size="small"
        value={getMonthDisplay(values[name])}
        error={!!showError}
        helperText={showError || ""}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <InsertInvitationIcon color={disabled ? "disabled" : "action"} />
            </InputAdornment>
          ),
        }}
        disabled={disabled}
        {...bindTrigger(disabled ? {} : popupState)}
      />

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography fontWeight={500}>{getMonthName(values[name])}</Typography>
            </Grid>
            {Array.from({ length: 12 }, (_, i) => {
              const monthNum = String(i + 1).padStart(2, "0");
              const monthName = moment()
                .month(i)
                .format("MMM");

              return (
                <Grid key={i} size={4}>
                  <Button
                    fullWidth
                    variant={values[name] === monthNum ? "contained" : "text"}
                    onClick={() => handleMonthSelect(monthNum)}
                    sx={{
                      ...styles.monthButton,
                      ...getButtonStyle(values[name] === monthNum),
                    }}
                  >
                    {`${monthName} (${monthNum})`}
                  </Button>
                </Grid>
              );
            })}
            <Grid display="flex" justifyContent="center" size={12}>
              <Button
                variant={values[name] === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{
                  ...styles.monthButton,
                  ...getButtonStyle(values[name] === "N/A"),
                }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </div>
  );
}
